import React, { useLayoutEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';

export function tooltipPosition(
  x: number,
  y: number,
  width: number,
  height: number,
  bounds: { left: number; top: number; right: number; bottom: number },
) {
  return {
    left: Math.max(bounds.left + 8, Math.min(x + 12, bounds.right - width - 8)),
    top: Math.max(bounds.top + 8, Math.min(y - height - 12, bounds.bottom - height - 8)),
  };
}

export const tooltipPaperStyle = (maxWidth = 380) => ({
  maxWidth: `min(100%, ${maxWidth}px)`,
  width: 'fit-content',
  boxSizing: 'border-box',
});

export default function ChartTooltip({ children, maxWidth = 380 }: { children: React.ReactNode; maxWidth?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number; maxWidth: number; maxHeight: number }>();
  useLayoutEffect(() => {
    const el = ref.current;
    const chart = el?.closest('.recharts-wrapper');
    if (!el || !chart) return;
    const place = (event?: MouseEvent) => {
      const r = (chart.closest('.MuiCard-root') || chart).getBoundingClientRect();
      const bounds = {
        left: Math.max(0, r.left),
        top: Math.max(0, r.top),
        right: Math.min(window.innerWidth, r.right),
        bottom: Math.min(window.innerHeight, r.bottom),
      };
      const maxWidth = Math.max(1, bounds.right - bounds.left - 16);
      const maxHeight = Math.max(1, bounds.bottom - bounds.top - 16);
      const size = el.getBoundingClientRect();
      setPosition({
        ...tooltipPosition(
          event?.clientX ?? r.left + r.width / 2,
          event?.clientY ?? r.top + r.height / 2,
          Math.min(size.width, maxWidth),
          Math.min(size.height, maxHeight),
          bounds,
        ),
        maxWidth,
        maxHeight,
      });
    };
    place();
    chart.addEventListener('mousemove', place);
    window.addEventListener('resize', place);
    return () => {
      chart.removeEventListener('mousemove', place);
      window.removeEventListener('resize', place);
    };
  }, []);
  return (
    <Box
      ref={ref}
      data-testid="chart-tooltip"
      sx={{
        position: 'fixed',
        ...position,
        visibility: position ? 'visible' : 'hidden',
        overflow: 'auto',
        pointerEvents: 'none',
        zIndex: 10,
        '& > .MuiPaper-root': tooltipPaperStyle(maxWidth),
      }}
    >
      {children}
    </Box>
  );
}
